import apiClient from '../api/apiClient'
import { scopeInstanceService } from './scopeInstanceService'
import type {
    PotentialHRPartner,
    GroupedHRResponse,
    PotentialHRPartnersParams,
    HRPriorityGroup,
} from '../types/orgHierarchy'
import type { ScopeInstance } from '../types/admin'


/**
 * Get the parent chain (ancestors) of a scope instance
 * Returns array of scope instances from the given instance up to the root
 * Fetches instances on-demand instead of loading all at once
 */
const getParentChain = async (
    scopeInstanceId: string
): Promise<ScopeInstance[]> => {
    const chain: ScopeInstance[] = []
    const visited = new Set<string>()
    let currentId: string | null = scopeInstanceId

    // Traverse up the hierarchy, fetching each instance as needed
    while (currentId && !visited.has(currentId)) {
        visited.add(currentId)

        try {
            const instance = await scopeInstanceService.getById(currentId)
            chain.push(instance)
            currentId = instance.parent_instance_id
        } catch (error) {
            console.warn(`Failed to fetch instance ${currentId}:`, error)
            break
        }

        // Safety limit to prevent infinite loops
        if (chain.length > 20) {
            console.warn('Parent chain exceeded 20 levels, stopping traversal')
            break
        }
    }

    return chain
}

/**
 * Transform HR head API response to PotentialHRPartner format
 */
const transformHRHead = (
    hrHead: any,
    scopeInstance: ScopeInstance,
    priorityGroup: HRPriorityGroup
): PotentialHRPartner => {
    return {
        user_id: hrHead.user_id || 0,
        user_uuid: hrHead.user_id || hrHead.user_uuid || '',
        fname: hrHead.fname || '',
        lname: hrHead.lname || '',
        email: hrHead.email || '',
        job_title: hrHead.job_title || null,
        scope_name: scopeInstance.name,
        scope_instance_id: scopeInstance.scope_instance_id,
        priority_group: priorityGroup,
        is_primary: hrHead.is_primary || false,
    }
}

function getGroupLabel(priority: HRPriorityGroup): string {
    switch (priority) {
        case 1:
            return 'Same Unit'
        case 2:
            return 'Parent Unit'
        case 3:
            return 'Higher Units'
        case 4:
            return 'Other Units'
        default:
            return 'Other'
    }
}

export const hrSuggestionService = {
    /**
     * Get potential HR partners for an employee based on org hierarchy
     * Returns HR from the selected unit and all parent units (going upward)
     * If no scope_instance_id is provided, returns empty result
     */
    async getPotentialHRPartners(
        params: PotentialHRPartnersParams
    ): Promise<PotentialHRPartner[] | GroupedHRResponse> {
        const scopeInstanceId = params.scope_instance_id?.trim()

        // If no scope provided, return empty result
        if (!scopeInstanceId) {
            return params.grouped
                ? { suggested: null, groups: [] }
                : []
        }

        try {
            // Get the parent chain (selected unit + all parents going up)
            // Fetches instances on-demand to avoid memory issues
            const parentChain = await getParentChain(scopeInstanceId)

            if (parentChain.length === 0) {
                // No units found in hierarchy
                return params.grouped
                    ? { suggested: null, groups: [] }
                    : []
            }

            // Fetch HR heads for each unit in the chain
            const hrPartnersMap = new Map<string, PotentialHRPartner>()
            let primaryHR: PotentialHRPartner | null = null

            for (let i = 0; i < parentChain.length; i++) {
                const unit = parentChain[i]
                const priorityGroup = (Math.min(i + 1, 4)) as HRPriorityGroup

                try {
                    const hrHeads = await scopeInstanceService.getHrHeads(
                        unit.scope_instance_id
                    )

                    if (hrHeads && hrHeads.length > 0) {
                        for (const hrHead of hrHeads) {
                            const hrPartner = transformHRHead(hrHead, unit, priorityGroup)

                            // Store by user_uuid to avoid duplicates
                            const key = hrPartner.user_uuid
                            if (!hrPartnersMap.has(key)) {
                                hrPartnersMap.set(key, hrPartner)

                                // Set primary HR (highest priority one)
                                if (hrHead.is_primary && !primaryHR && i === 0) {
                                    primaryHR = hrPartner
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.warn(
                        `Failed to fetch HR heads for unit ${unit.scope_instance_id}:`,
                        error
                    )
                    // Continue with other units even if one fails
                }
            }

            const allHRPartners = Array.from(hrPartnersMap.values())

            // Apply limit if specified
            const limitedHRPartners = params.limit
                ? allHRPartners.slice(0, params.limit)
                : allHRPartners

            // Return grouped or flat response
            if (params.grouped) {
                // Group by priority
                const groupsMap = new Map<HRPriorityGroup, PotentialHRPartner[]>()

                limitedHRPartners.forEach((hr) => {
                    const existing = groupsMap.get(hr.priority_group) || []
                    groupsMap.set(hr.priority_group, [...existing, hr])
                })

                // Create groups with labels
                const groups = Array.from(groupsMap.entries())
                    .sort(([a], [b]) => a - b)
                    .map(([priority, hrs]) => ({
                        label: getGroupLabel(priority),
                        priority,
                        hrPartners: hrs,
                    }))

                return {
                    suggested: primaryHR || (limitedHRPartners.length > 0 ? limitedHRPartners[0] : null),
                    groups,
                }
            }

            return limitedHRPartners
        } catch (error) {
            console.error('Failed to get potential HR partners:', error)
            throw error
        }
    },

    /**
     * Get the top suggested HR partner for an employee
     */
    async getSuggestedHRPartner(
        scopeInstanceId?: string,
        employeeId?: string
    ): Promise<{ suggested: PotentialHRPartner | null }> {
        if (!scopeInstanceId?.trim()) {
            return { suggested: null }
        }

        try {
            const result = await hrSuggestionService.getPotentialHRPartners({
                scope_instance_id: scopeInstanceId,
                employee_id: employeeId,
                grouped: true,
                limit: 1,
            })

            if (typeof result === 'object' && 'suggested' in result) {
                return { suggested: result.suggested }
            }

            return { suggested: null }
        } catch (error) {
            console.error('Failed to get suggested HR partner:', error)
            return { suggested: null }
        }
    },
}
